import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";
import { sanitizeUrlInput } from "../lib/urlUtils";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Globe, Zap, CheckCircle } from "lucide-react";

const OrderPage = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { language } = useLanguage();
  const [site, setSite] = useState(null);
  const [targetUrl, setTargetUrl] = useState("");
  const [anchorText, setAnchorText] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const siteRes = await api.get(`/sites/${siteId}`);
      setSite(siteRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error(language === "tr" ? "Site bilgileri yüklenemedi" : "Failed to load site info");
      navigate("/sites");
    } finally {
      setLoading(false);
    }
  }, [language, navigate, siteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const cleanTarget = sanitizeUrlInput(targetUrl);
    if (!cleanTarget || !anchorText) {
      toast.error(language === "tr" ? "Tüm alanları doldurun" : "Fill all fields");
      return;
    }

    setSubmitting(true);
    
    try {
      await api.post("/orders", {
        site_id: siteId,
        target_url: cleanTarget,
        anchor_text: anchorText
      });

      toast.success(language === "tr" ? "Backlink siparişi oluşturuldu!" : "Backlink order created!");
      await refreshUser();
      navigate("/dashboard");
    } catch (error) {
      console.error("Order error:", error);
      toast.error(error.response?.data?.detail || (language === "tr" ? "Sipariş oluşturulamadı" : "Order failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary font-mono animate-pulse">
          {language === "tr" ? "Yükleniyor..." : "Loading..."}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid-texture p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/sites" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8 font-mono text-sm">
          <ArrowLeft className="w-4 h-4" />
          {language === "tr" ? "SİTELERE DÖN" : "BACK TO SITES"}
        </Link>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Site Info */}
          <div className="glass p-6">
            <h2 className="font-mono font-bold text-lg mb-4 uppercase flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              {language === "tr" ? "Site Bilgileri" : "Site Information"}
            </h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground font-mono mb-1">
                  {language === "tr" ? "Site Adı" : "Site Name"}
                </p>
                <p className="font-mono font-bold text-lg">{site?.name}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground font-mono mb-1">URL</p>
                <a 
                  href={site?.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline flex items-center gap-1 font-mono"
                >
                  {site?.url} <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              <div className="grid grid-cols-4 gap-4 pt-4 border-t border-white/10">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground font-mono">DA</p>
                  <p className="font-mono font-bold text-xl text-primary">{site?.da}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground font-mono">PA</p>
                  <p className="font-mono font-bold text-xl">{site?.pa}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground font-mono">SS</p>
                  <p className="font-mono font-bold text-xl">{site?.spam_score}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground font-mono">
                    {language === "tr" ? "TRAFİK" : "TRAFFIC"}
                  </p>
                  <p className="font-mono font-bold text-xl">{site?.traffic > 1000 ? `${(site.traffic/1000).toFixed(0)}K` : site?.traffic}</p>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <span className="px-3 py-1 bg-white/5 text-sm font-mono">{site?.category}</span>
                <span className="px-3 py-1 bg-white/5 text-sm font-mono">{site?.language}</span>
                <span className="px-3 py-1 bg-primary/20 text-primary text-sm font-mono">{site?.link_type}</span>
              </div>
            </div>
          </div>

          {/* Order Form */}
          <div className="glass p-6">
            <h2 className="font-mono font-bold text-lg mb-4 uppercase flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              {language === "tr" ? "Backlink Siparişi" : "Backlink Order"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-mono text-sm uppercase tracking-wider">
                  {language === "tr" ? "Hedef URL" : "Target URL"}
                </Label>
                <Input
                  type="url"
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(sanitizeUrlInput(e.target.value))}
                  placeholder="https://siteniz.com/sayfa"
                  required
                  className="bg-black/50 border-white/10 rounded-none font-mono h-12"
                  data-testid="target-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  {language === "tr" ? "Backlink'in yönlendireceği sayfa" : "Page where backlink will redirect to"}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="font-mono text-sm uppercase tracking-wider">Keyword</Label>
                <Input
                  type="text"
                  value={anchorText}
                  onChange={(e) => setAnchorText(e.target.value)}
                  placeholder={language === "tr" ? "Anahtar kelime" : "Keyword"}
                  required
                  className="bg-black/50 border-white/10 rounded-none font-mono h-12"
                  data-testid="anchor-text-input"
                />
                <p className="text-xs text-muted-foreground">
                  {language === "tr" ? "Link'te görünecek metin (dofollow, gizli)" : "Text to display in link (dofollow, hidden)"}
                </p>
              </div>

              <div className="pt-4 border-t border-white/10 space-y-4">
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 text-center">
                  <div className="text-emerald-300 font-mono font-bold">
                    {language === "tr" ? "ABONELİK İLE SINIRSIZ SİPARİŞ" : "UNLIMITED ORDERS WITH SUBSCRIPTION"}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full font-mono uppercase tracking-wider bg-primary text-black hover:bg-primary/90 rounded-none h-12"
                  data-testid="submit-order-btn"
                >
                  {submitting ? (language === "tr" ? "İşleniyor..." : "Processing...") : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {language === "tr" ? "SİPARİŞ OLUŞTUR" : "CREATE ORDER"}
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                {language === "tr" 
                  ? "Link dofollow olarak, ziyaretçilere görünmeyecek şekilde eklenecektir" 
                  : "Link will be added as dofollow, hidden from visitors"}
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderPage;
